import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';

import { analyser, type Bloc, type Inline } from './analyse-markdown.js';

/** Rendu React du Markdown de l'aide (aucun HTML injecté). */

function enLigne(items: Inline[]): ReactNode[] {
  return items.map((n, i) => {
    switch (n.t) {
      case 'texte':
        return n.v;
      case 'gras':
        return <strong key={i}>{enLigne(n.c)}</strong>;
      case 'italique':
        return <em key={i}>{enLigne(n.c)}</em>;
      case 'code':
        return <code key={i}>{n.v}</code>;
      case 'lien':
        // Écran d'Oasis : navigation interne ; ailleurs : lien ordinaire, nouvel onglet.
        return n.href.startsWith('/') ? (
          <Link key={i} to={n.href}>
            {enLigne(n.c)}
          </Link>
        ) : n.href.startsWith('https://') ? (
          <a key={i} href={n.href} target="_blank" rel="noopener noreferrer">
            {enLigne(n.c)}
          </a>
        ) : (
          <span key={i}>{enLigne(n.c)}</span>
        );
    }
  });
}

function bloc(b: Bloc, i: number, decalage: number): ReactElement {
  switch (b.t) {
    case 'titre': {
      const niveau = Math.min(6, b.niveau + decalage);
      const Balise = `h${String(niveau)}` as 'h2';
      return <Balise key={i}>{enLigne(b.c)}</Balise>;
    }
    case 'paragraphe':
      return <p key={i}>{enLigne(b.c)}</p>;
    case 'liste': {
      const items = b.items.map((it, j) => <li key={j}>{enLigne(it)}</li>);
      return b.ordonnee ? <ol key={i}>{items}</ol> : <ul key={i}>{items}</ul>;
    }
    case 'note':
      return (
        <aside key={i} className="o-aide-note">
          {b.c.map((x, j) => bloc(x, j, decalage))}
        </aside>
      );
    case 'code':
      return (
        <pre key={i}>
          <code>{b.v}</code>
        </pre>
      );
    case 'tableau':
      return (
        <div key={i} className="o-aide-tableau">
          <table>
            <thead>
              <tr>
                {b.entete.map((c, j) => (
                  <th key={j}>{enLigne(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.lignes.map((l, j) => (
                <tr key={j}>
                  {l.map((c, k) => (
                    <td key={k}>{enLigne(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

/**
 * `decalage` : niveaux ajoutés aux titres (un `#` du texte devient `h2` sous le titre de la
 * page avec un décalage de 1).
 */
export function Markdown({
  source,
  decalage = 1,
}: {
  source: string;
  decalage?: number;
}): ReactElement {
  return <div className="o-aide-texte">{analyser(source).map((b, i) => bloc(b, i, decalage))}</div>;
}
